import { useGetTokensAndBalances } from "@umami/state";
import { type BridgeDirection, type LiFiQuoteResponse, type ZeroXQuoteResponse } from "@umami/swap";
import { useEffect, useRef, useState } from "react";

import { FeeEstimationService } from "../components/FeeEstimation";

interface UseFeeEstimationParams {
  direction: BridgeDirection;
  provider?: string;
  zeroXQuote?: ZeroXQuoteResponse | null;
  lifiQuote?: LiFiQuoteResponse | null;
  layerZeroQuote?: any | null;
  estimateFee: (direction: BridgeDirection) => Promise<{ nativeFee: bigint; zroFee: bigint }>;
  showNetworkFee: boolean;
  onFeeEstimateChange?: (
    feeEstimate: {
      nativeFee: bigint;
      zroFee: bigint;
      nativeFees?: Array<{
        amount: bigint;
        symbol: string;
        chainId?: string;
      }>;
    } | null
  ) => void;
  sellToken?: any;
  buyToken?: any;
}

interface UseFeeEstimationReturn {
  feeDisplay: {
    amount: number;
    symbol: string;
    usdValue: number;
  } | null;
  isLoading: boolean;
}

export const useFeeEstimation = ({
  direction,
  provider,
  zeroXQuote,
  lifiQuote,
  layerZeroQuote,
  estimateFee,
  showNetworkFee,
  onFeeEstimateChange,
  sellToken,
  buyToken,
}: UseFeeEstimationParams): UseFeeEstimationReturn => {
  const { getTokenUsdPrice } = useGetTokensAndBalances();
  const getTokenUsdPriceRef = useRef(getTokenUsdPrice);
  getTokenUsdPriceRef.current = getTokenUsdPrice;

  const [feeDisplay, setFeeDisplay] = useState<{
    amount: number;
    symbol: string;
    usdValue: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!showNetworkFee) {
      setFeeDisplay(null);
      setIsLoading(false);

      return;
    }

    const handleFeeEstimation = async () => {
      try {
        setIsLoading(true);

        const feeEstimate = await FeeEstimationService.estimateFee({
          direction,
          provider,
          zeroXQuote,
          lifiQuote,
          layerZeroQuote,
          estimateFee,
          getTokenUsdPrice: getTokenUsdPriceRef.current,
          sellToken,
          buyToken,
        });

        if (feeEstimate) {
          setFeeDisplay({
            amount: feeEstimate.amount,
            symbol: feeEstimate.symbol,
            usdValue: feeEstimate.usdValue,
          });

          if (onFeeEstimateChange) {
            onFeeEstimateChange({
              nativeFee: feeEstimate.nativeFee,
              zroFee: feeEstimate.zroFee,
              nativeFees: feeEstimate.nativeFees,
            });
          }
        } else {
          setFeeDisplay(null);
        }
      } catch (error) {
        console.error("Error fetching fee estimate:", error);
        setFeeDisplay(null);
      } finally {
        setIsLoading(false);
      }
    };

    void handleFeeEstimation();
  }, [
    direction,
    provider,
    zeroXQuote,
    lifiQuote,
    layerZeroQuote,
    estimateFee,
    showNetworkFee,
    onFeeEstimateChange,
    sellToken,
    buyToken,
  ]);

  return {
    feeDisplay,
    isLoading,
  };
};
